import { useQuery, useQueryClient } from "react-query";
import { CATEGORIES, SURVEYS, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { Badge, Button, Card, Modal, Pagination, Tabs } from "flowbite-react";
import { useApplication } from "../../contexts/ApplicationContext";
import { useRef, useState } from "react";
import { Icon } from "@mui/material";
import { IoMdAdd } from "react-icons/io";
import CardLoading from "../CardLoading";
import { formatToReal, getFirstLettersName } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FaCalendarDay } from "react-icons/fa";
import { IoPersonOutline, IoSettingsSharp } from "react-icons/io5";
import EmptyTable from "./EmptyTable";
import ModalDelete from "../modals/ModalDelete";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaTriangleExclamation } from "react-icons/fa6";

const SurveysTable = () => {
  const queryClient = useQueryClient();
  const { userPermission, user } = useApplication();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [typeSurvey, setTypeSurvey] = useState(0);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isOpenModalDelete, setOpenModalDelete] = useState(false);
  const [modalConfirmNewSurvey, setModalConfirmNewSurvey] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const tabsRef = useRef(null);

  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);

  const {
    isLoading,
    error,
    data: surveys,
  } = useQuery(["surveys", currentPage, typeSurvey], async () => {
    const { data: items } = await axios.get(
      `${SURVEYS.GET_ALL_SURVEY}?page=${currentPage}&status=${typeSurvey}`
    );

    setTotalPages(items.data.totalPages || 1);
    return items.data;
  });

  const handleDeleteSurvey = (survey) => {
    setSelectedSurvey(survey);
    setOpenModalDelete(true);
  };

  const deleteSurvey = async () => {
    try {
      setIsLoadingDelete(true);
      await axios.delete(`${SURVEYS.GET_SURVEY}/${selectedSurvey.id}`);

      queryClient.invalidateQueries(["surveys", currentPage]);
      setOpenModalDelete(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  return (
    <>
      {user?.Company?.hasSurvey ? (
        <>
          <div className="flex justify-end mb-3 mt-2 gap-2">
            <Button
              className="primary ml-auto md:ml-0 mb-5 md:mb-0 fixed md:relative bottom-4 md:bottom-0 right-6 md:right-0 z-10 md:z-0 rounded-full md:rounded-md shadow-lg md:shadow-md h-14 md:h-auto w-14 md:w-auto flex md:block justify-center items-center"
              variant="contained"
              disabled={isLoading ? true : false}
              onClick={() => setModalConfirmNewSurvey(true)}
            >
              <IoMdAdd className="block md:hidden text-2xl" />

              <span className="hidden md:block">Iniciar nova vistoria</span>
            </Button>
            {["ADMIN", "POWER_USER"]?.includes(userPermission) && (
              <Button
                className="bg-gray-500 rounded-md flex justify-center items-center"
                variant="contained"
                disabled={isLoading ? true : false}
                onClick={() => navigate("/survey/config")}
              >
                <span className="block">
                  <IoSettingsSharp />
                </span>
              </Button>
            )}
          </div>
          <div className="bg-white p-3 rounded-md shadow-md">
            <Tabs
              aria-label="Tabs with underline"
              variant="underline"
              ref={tabsRef}
              onActiveTabChange={(tab) => setTypeSurvey(tab)}
            >
              <Tabs.Item active title="Em andamento">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                  {!isLoading ? (
                    <>
                      <>
                        {surveys?.map((survey, index) => (
                          <Card key={index} className="relative">
                            <div className="flex gap-3 items-center">
                              <div
                                className="flex items-center justify-center"
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  fontSize: "18px",
                                  color: "white",
                                  borderRadius: "50%",
                                  minWidth: "40px",
                                  background: `var(--primary)`,
                                }}
                              >
                                {survey.ownerName
                                  ? getFirstLettersName(survey.ownerName)
                                  : "?"}
                              </div>

                              <div className="flex gap-2 items-center">
                                <h2 className="text-sm">
                                  Proprietário: {survey?.ownerName}
                                </h2>
                                {survey?.status == 0 ? (
                                  <Badge
                                    color="warning"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Em progresso
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="green"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Finalizado
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 text-gray-500 text-xs items-center">
                              <IoPersonOutline fontSize={14} />
                              <span className="text-xs truncate">
                                Vistoriador: {survey?.surveyor?.name}
                              </span>
                            </div>
                            <div className="flex gap-1 text-gray-500 mr-20 items-center">
                              <FaCalendarDay fontSize={14} />
                              <span className="text-xs truncate mr-12">
                                Data de início:{" "}
                                {moment(survey.createdAt).format(
                                  "DD/MM/yyyy | HH:mm"
                                ) || "-"}
                              </span>
                            </div>
                            <div className="flex absolute bottom-5 right-3 gap-8">
                              <Icon
                                fontSize="small"
                                className="color-primary cursor-pointer"
                                onClick={() =>
                                  navigate(`/survey/new`, { state: survey })
                                }
                              >
                                edit
                              </Icon>
                              <Icon
                                fontSize="small"
                                className="text-gray-500 cursor-pointer"
                                onClick={() => handleDeleteSurvey(survey)}
                              >
                                delete
                              </Icon>
                            </div>
                          </Card>
                        ))}
                      </>
                    </>
                  ) : (
                    Array.from({ length: 12 }).map((_, index) => (
                      <CardLoading key={index} />
                    ))
                  )}
                </div>
                {surveys?.length === 0 && (
                  <EmptyTable icon="article">
                    Nenhum laudo em andamento.
                  </EmptyTable>
                )}
              </Tabs.Item>
              <Tabs.Item title="Finalizados">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                  {!isLoading ? (
                    <>
                      <>
                        {surveys?.map((survey, index) => (
                          <Card key={index} className="relative">
                            <div className="flex gap-3 items-center">
                              <div
                                className="flex items-center justify-center"
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  fontSize: "18px",
                                  color: "white",
                                  borderRadius: "50%",
                                  minWidth: "40px",
                                  background: `var(--primary)`,
                                }}
                              >
                                {survey.ownerName
                                  ? getFirstLettersName(survey.ownerName)
                                  : "?"}
                              </div>

                              <div className="flex gap-2 items-center">
                                <h2 className="text-sm">
                                  Proprietário: {survey?.ownerName}
                                </h2>
                                {survey?.status == 0 ? (
                                  <Badge
                                    color="warning"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Em progresso
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="green"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Finalizado
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 text-gray-500 text-xs items-center">
                              <IoPersonOutline fontSize={14} />
                              <span className="text-xs truncate">
                                Vistoriador: {survey?.surveyor?.name}
                              </span>
                            </div>
                            <div className="flex gap-1 text-gray-500 mr-20 items-center">
                              <FaCalendarDay fontSize={14} />
                              <span className="text-xs truncate mr-12">
                                Data de início:{" "}
                                {moment(survey.createdAt).format(
                                  "DD/MM/yyyy | HH:mm"
                                ) || "-"}
                              </span>
                            </div>
                            <div className="flex absolute bottom-5 right-3 gap-8">
                              <a
                                target="_blank"
                                href={survey?.pdfURL}
                                className="m-0 p-0"
                              >
                                <Icon
                                  fontSize="small"
                                  className="color-primary cursor-pointer"
                                >
                                  visibility
                                </Icon>
                              </a>
                              <Icon
                                fontSize="small"
                                className="color-primary cursor-pointer"
                                onClick={() =>
                                  navigate(`/survey/new`, { state: survey })
                                }
                              >
                                edit
                              </Icon>
                            </div>
                          </Card>
                        ))}
                      </>
                    </>
                  ) : (
                    Array.from({ length: 12 }).map((_, index) => (
                      <CardLoading key={index} />
                    ))
                  )}
                </div>
                {surveys?.length === 0 && (
                  <EmptyTable icon="article">
                    Nenhum laudo finalizado.
                  </EmptyTable>
                )}
              </Tabs.Item>
            </Tabs>

            {totalPages > 1 && (
              <div className="flex overflow-x-auto justify-center mt-3">
                <Pagination
                  layout="pagination"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  previousLabel=""
                  nextLabel=""
                  showIcons
                />
              </div>
            )}
          </div>

          <ModalDelete
            title={`Deseja excluir este laudo?`}
            isOpen={isOpenModalDelete}
            setOpenModal={setOpenModalDelete}
            isLoading={isLoadingDelete}
            handleDelete={deleteSurvey}
          />

          <Modal
            show={modalConfirmNewSurvey}
            size="md"
            onClose={() => setModalConfirmNewSurvey(false)}
            popup
          >
            <Modal.Header />
            <Modal.Body>
              <div className="text-center">
                <FaTriangleExclamation className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
                <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                  Ao iniciar uma nova vistoria, será debitado o valor de{" "}
                  <b>{formatToReal(user?.Company?.priceSurvey)}</b> da vistoria
                  apenas no momento da emissão do laudo.
                </h3>
                <div className="flex justify-center gap-2">
                  <Button
                    className="primary"
                    onClick={() => navigate("/survey/new")}
                  >
                    Prosseguir
                  </Button>
                  <Button
                    color="gray"
                    onClick={() => setModalConfirmNewSurvey(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        </>
      ) : (
        <ForbiddenQueriesPermission />
      )}
    </>
  );
};

export default SurveysTable;
